import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Users, BookOpen, CalendarDays, Sparkles, BellRing, BellOff } from "lucide-react";
import { pushSupported, getSubscription, subscribePush, unsubscribePush } from "@/lib/push";
import { toast } from "sonner";

const ICONS = {
  new_member: Users,
  new_blog: BookOpen,
  new_event: CalendarDays,
  event_reminder: Sparkles,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const ref = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []); setUnread(data.unread || 0);
    } catch (err) { /* silent */ }
  };

  useEffect(() => {
    load();
    (async () => { try { setPushOn(!!(await getSubscription())); } catch (e) { /* ignore */ } })();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (err) { /* silent */ }
  };

  const markOne = async (n) => {
    if (!n.read) { try { await api.patch(`/notifications/${n.notif_id}/read`); } catch (err) { /* silent */ } }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Push notifikasi dimatikan"); }
      else { await subscribePush(); setPushOn(true); toast.success("Push notifikasi diaktifkan"); }
    } catch (e) { toast.error(e?.message || "Gagal mengatur push"); }
    finally { setPushBusy(false); }
  };

  return (
    <div ref={ref} className="relative" data-testid="notif-bell-wrap">
      <button onClick={() => { setOpen(!open); if (!open) load(); }} className="relative w-10 h-10 rounded-full hover:bg-bizma-muted grid place-items-center text-bizma-ink" data-testid="notif-bell-btn" aria-label="Notifikasi">
        <Bell size={20}/>
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-bizma-terracotta text-white text-[10px] font-bold grid place-items-center" data-testid="notif-unread-count">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <div className="fixed right-2 sm:right-6 left-2 sm:left-auto top-16 sm:top-14 sm:absolute sm:mt-0 sm:w-80 max-h-[70vh] bg-white rounded-2xl border border-bizma-border shadow-xl overflow-hidden z-40 flex flex-col mx-auto" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-bizma-border">
            <div className="font-display font-semibold">Notifikasi</div>
            {unread > 0 && <button onClick={markAll} className="text-xs text-bizma-terracotta font-medium" data-testid="notif-mark-all">Tandai semua dibaca</button>}
          </div>
          {pushSupported() && (
            <button onClick={togglePush} disabled={pushBusy}
              className={`flex items-center gap-2 px-4 py-2.5 border-b border-bizma-border text-sm hover:bg-bizma-muted transition-colors ${pushOn ? "text-bizma-sage" : "text-bizma-ink"}`}
              data-testid="push-toggle-btn">
              {pushOn ? <BellRing size={16}/> : <BellOff size={16}/>}
              <span className="flex-1 text-left">{pushOn ? "Push aktif — klik untuk matikan" : "Aktifkan push notifikasi"}</span>
              {pushBusy && <span className="text-xs text-bizma-subtext">...</span>}
            </button>
          )}
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && <div className="p-6 text-center text-sm text-bizma-subtext" data-testid="notif-empty">Belum ada notifikasi.</div>}
            {items.map((n) => {
              const Ic = ICONS[n.type] || Bell;
              return (
                <Link key={n.notif_id} to={n.link || "#"} onClick={() => { markOne(n); setOpen(false); }}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-bizma-border/60 hover:bg-bizma-muted transition-colors ${!n.read ? "bg-bizma-cream" : ""}`}
                  data-testid={`notif-item-${n.notif_id}`}>
                  <div className="w-9 h-9 rounded-full bg-bizma-sage/15 text-bizma-sage grid place-items-center shrink-0"><Ic size={16}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{n.title}</div>
                    <div className="text-xs text-bizma-subtext line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-bizma-subtext mt-1">{new Date(n.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</div>
                  </div>
                  {!n.read && <div className="w-2 h-2 rounded-full bg-bizma-terracotta mt-2 shrink-0"/>}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
